import * as React from "react"
import { useTooltip } from "@reach/tooltip"
import { useTransition, animated } from "react-spring"

import TooltipContent from "./TooltipContent"
import { DisableReachStyleCheck } from "../../utils/helpers/DisableReachStyleCheck"
import { TooltipPosition } from "./types"

const AnimatedTooltipContent = animated(TooltipContent)

export type TooltipProps = Omit<
  React.ComponentPropsWithoutRef<"div">,
  "ref" | "label" | "children"
> & {
  label: React.ReactNode
  children: React.ReactNode
  position?: TooltipPosition
}

export default function Tooltip({
  children,
  id,
  label,
  position = "top",
  ...rest
}: TooltipProps) {
  // COPIED FROM @reach/tooltip source:
  //
  // We need to pass some properties from the child into useTooltip
  // to make sure users can maintain control over the trigger's ref and events
  const child = React.Children.only(children) as any

  const [trigger, tooltipParams, isVisible] = useTooltip({
    id,
    onMouseEnter: child.props.onMouseEnter,
    onMouseMove: child.props.onMouseMove,
    onMouseLeave: child.props.onMouseLeave,
    onFocus: child.props.onFocus,
    onBlur: child.props.onBlur,
    onKeyDown: child.props.onKeyDown,
    onMouseDown: child.props.onMouseDown,
    ref: child.ref,
  })

  const transitions = useTransition(isVisible ? tooltipParams : null, null, {
    // unit for the to-be-interpolated property is crucial for this to work
    // ref. https://github.com/pmndrs/react-spring/issues/779#issuecomment-520185113
    from: { opacity: 0, transform: "translate3d(0, 0px, 0)" },
    enter: {
      opacity: 1,
      transform:
        position === "top"
          ? "translate3d(0, -4px, 0)"
          : "translate3d(0, 4px, 0)",
    },
    leave: { opacity: 0, transform: "translate3d(0, 0, 0)" },
    config: {
      tension: 200,
      friction: 20,
    },
  })

  return (
    <React.Fragment>
      {React.cloneElement(child, trigger as any)}

      {transitions.map(({ item: tooltip, props: transitionStyles, key }) => {
        if (!tooltip) {
          return null
        }

        return (
          <AnimatedTooltipContent
            key={key}
            label={label}
            tooltipParams={tooltip}
            style={transitionStyles}
            position={position}
            {...rest}
          />
        )
      })}
      <DisableReachStyleCheck reachComponent="tooltip" />
    </React.Fragment>
  )
}
